// Sun shadows through three.js's cascaded shadow maps (CSM): several shadow maps, each covering a slice of the camera's view, so
// the ground at your feet gets centimetre texels while the far island still gets a map. Replaces the single world-wide sun light.
//
// What this adds on top of three's CSM:
//  - keeps a material's own onBeforeCompile (CSM would overwrite it) and keeps its shader cache key distinct;
//  - sets up every lit material in the scene before each render, so nothing is left lit by all the cascade lights at once;
//  - per-cascade depth range and bias, so a 1 cm texel is not given a bias sized for a 1 km depth range;
//  - redraws each cascade's map only when it must (sun moved, casters changed, or the cascade's window moved by a texel), and not
//    at all while the sun is down.
import * as THREE from 'three';
import { CSM } from 'three/examples/jsm/csm/CSM.js';
import { installCloudShadowChunks, makeCloudShadowUniforms } from './cloudShadow';

export interface SunShadowOptions {
  /** metres from the camera where each cascade ends; the last one is how far shadows reach */
  splitsM?: number[];
  mapSize?: number;
  /** how far up-sun of a cascade's slice casters are still included. A caster of height h at sun elevation e shades ground h / tan(e)
   *  away, so this wants to be about (tallest caster) / sin(lowest elevation worth shading). */
  marginM?: number;
  /** depth bias in metres (converted to the fraction three.js wants, per cascade), at least this much and at least a tenth of a texel */
  biasM?: number;
  /** normal offset, in texels */
  normalBiasTexels?: number;
  /** blend between cascades */
  fade?: boolean;
  /** least time between redraws caused by changed casters, per cascade. A far cascade need not follow a moving body every server frame. */
  dirtyMinMs?: number[];
}

const LIT_FLAGS = ['isMeshStandardMaterial', 'isMeshPhysicalMaterial', 'isMeshLambertMaterial', 'isMeshPhongMaterial', 'isMeshToonMaterial'];


/** The texture units a lit material's fragment program reads, as far as can be told before it is built: the samplers its own source declares (an array counts its length), the shadow maps, and the maps three adds for the
 *  material's flags. An estimate that errs on the high side, so a program that is near the limit is left as it was. */
function samplerUnitsUsed(sh: any): number {
  const src: string = String(sh.fragmentShader ?? '').replace(/\/\/.*$/gm, '').replace(/\/\*[\s\S]*?\*\//g, '');
  let n = 0;
  const re = /\buniform\s+(?:(?:highp|mediump|lowp)\s+)?sampler\w+\s+([^;]+);/g;
  for (let m = re.exec(src); m; m = re.exec(src)) {
    for (const part of m[1]!.split(',')) { const a = /\[\s*(\d+)\s*\]/.exec(part); n += a ? parseInt(a[1]!, 10) : 1; }
  }
  if (sh.shadowMapEnabled) n += (sh.numDirLightShadows || 0) + (sh.numSpotLightShadows || 0) + (sh.numPointLightShadows || 0);
  for (const f of ['map', 'alphaMap', 'aoMap', 'lightMap', 'bumpMap', 'normalMap', 'displacementMap', 'emissiveMap', 'metalnessMap', 'roughnessMap', 'envMap', 'gradientMap', 'specularMap', 'clearcoatMap', 'sheenColorMap', 'transmission', 'iridescenceMap', 'anisotropyMap']) if (sh[f]) n += 1;
  return n;
}

export class SunShadows {
  readonly csm: CSM;
  readonly lights: THREE.DirectionalLight[];
  private readonly mapSize: number;
  private readonly margin: number;
  private readonly biasM: number;
  private readonly nbTexels: number;
  private readonly done = new WeakSet<THREE.Material>();
  private pending: boolean[] = [];                 // per cascade: casters changed since it was last drawn
  private lastDrawMs: number[] = [];
  private readonly dirtyMinMs: number[];
  private enabled = true;
  private intensity = 0;
  private readonly lastPos: THREE.Vector3[] = [];
  private readonly _q = new THREE.Vector3();
  private readonly lastDir = new THREE.Vector3(NaN, 0, 0);     // the direction the cascades are currently set to
  private readonly wantDir = new THREE.Vector3(-1, -1, -1).normalize();
  private camKey = '';
  private lastRefreshMs = 0;
  private readonly baseSplits: number[];
  private ringK = 1;                               // the cascades' scale (see setReach): a power of two, 1 with the eye near a surface
  /** redraws requested per cascade since construction (for an overlay or a test) */
  readonly redraws: number[];
  /** metres per texel of each cascade, as of the last update */
  readonly texelM: number[];
  /** The uniforms of the cloud shadow (cloudShadow.ts), which every material set up here has: hand them to CloudShadow's constructor. */
  readonly cloudUniforms = makeCloudShadowUniforms();
  private readonly cloudOk: boolean;

  constructor(private scene: THREE.Scene, private camera: THREE.PerspectiveCamera, opts: SunShadowOptions = {}) {
    const splits = opts.splitsM ?? [8, 30, 100, 300];
    this.baseSplits = splits.slice();
    this.mapSize = opts.mapSize ?? 2048;
    this.margin = opts.marginM ?? 200;
    this.biasM = opts.biasM ?? 0.003;
    this.nbTexels = opts.normalBiasTexels ?? 0.5;
    this.dirtyMinMs = opts.dirtyMinMs ?? [0, 0, 100, 250];
    this.csm = new CSM({
      camera, parent: scene, cascades: splits.length, maxFar: splits[splits.length - 1], mode: 'custom', shadowMapSize: this.mapSize,
      lightDirection: new THREE.Vector3(-1, -1, -1).normalize(), lightIntensity: 0, lightNear: 1, lightFar: 2000, lightMargin: this.margin,
      shadowBias: 0,
      customSplitsCallback: (_n: number, _near: number, far: number, target: number[]) => {
        target.length = 0; for (const d of this.baseSplits) target.push(Math.min(1, (d * this.ringK) / far));
      },
    });
    this.csm.fade = opts.fade ?? true;
    this.cloudOk = installCloudShadowChunks();                 // (after the CSM: its constructor puts its own light chunks in)
    this.lights = this.csm.lights;
    for (const l of this.lights) { l.shadow.autoUpdate = false; l.shadow.needsUpdate = true; }
    this.pending = this.lights.map(() => true); this.lastDrawMs = this.lights.map(() => -1e9);
    this.redraws = this.lights.map(() => 0);
    this.texelM = this.lights.map(() => 0);
    this.lights.forEach(() => this.lastPos.push(new THREE.Vector3(NaN, 0, 0)));
  }

  /**
   * Scale the cascades with the eye's distance to the nearest surface. The cascades are rings about the eye (ratio about 3 between neighbours); with the eye near the ground the first
   * ends 8 m out, and at 1 km up that ring would hold nothing but air, so all of them are scaled by one power of two until the first ends beyond the surface. The scale is 1 within
   * `nearestM` of a surface (the splits as given), grows in doublings (a change redraws every map once), and comes back down only when the eye is a quarter of the way to the next
   * step down, so hovering at a threshold does not redraw every frame.
   */
  setReach(nearestM: number) {
    if (!(nearestM >= 0) || !Number.isFinite(nearestM)) return;
    const first = this.baseSplits[0] ?? 8;
    const want = Math.pow(2, Math.max(0, Math.ceil(Math.log2(Math.max(1e-9, nearestM / first)))));
    let k = this.ringK;
    if (want > k || want * 4 <= k) k = want;
    if (k === this.ringK) return;
    this.ringK = k;
    this.csm.maxFar = (this.baseSplits[this.baseSplits.length - 1] ?? 300) * k;
    this.camKey = '';                                  // refit on the next update (the frustums are made again)
    this.markDirty();
  }
  /** the cascades' current scale (for an overlay or a test) */
  get scale() { return this.ringK; }

  /** The sun for this frame: unit vector toward the sun (three.js axes), beam colour, and intensity (0 = sun down). */
  setSun(toSun: THREE.Vector3, r: number, g: number, b: number, intensity: number) {
    this.wantDir.copy(toSun).multiplyScalar(-1).normalize();
    for (const l of this.lights) { l.color.setRGB(r, g, b); l.intensity = intensity; }
    if (intensity > 0 && this.intensity <= 0) this.markDirty();       // the sun came up: the maps are stale
    this.intensity = intensity;
  }

  /** Shadows on or off (key 3). Switching recompiles lit materials once, like toggling castShadow on a light does. */
  setEnabled(on: boolean) {
    if (on === this.enabled) return;
    this.enabled = on; for (const l of this.lights) l.castShadow = on; if (on) this.markDirty();
  }
  get isEnabled() { return this.enabled; }

  /** Something that casts changed (a body moved, terrain or roster changed). */
  markDirty() { this.pending.fill(true); }

  /** Call once per frame after the camera has its final pose and before renderer.render. Returns how many cascades were queued for a redraw. */
  update(nowMs: number, refreshEveryMs = 500): number {
    const cam = this.camera;
    const key = `${cam.fov}|${cam.aspect}|${cam.near}|${cam.far}`;
    if (key !== this.camKey) { this.camKey = key; this.csm.updateFrustums(); this.fitCascades(); }
    // The cascades keep their direction until the sun has moved far enough to shift a shadow visibly, then all of them follow
    // (and redraw). Shadow length changes as 1 / sin^2(elevation) of the angle moved, so the step shrinks as the sun gets low:
    // about 0.03 degrees at high sun down to 0.006 degrees near the horizon. Between steps the windows only slide with the camera.
    const sinE = Math.max(0.05, -this.wantDir.y);
    const step = Math.min(5e-4, Math.max(1e-4, 0.0033 * sinE * sinE));
    const dirMoved = !(this.lastDir.distanceToSquared(this.wantDir) <= step * step);
    if (dirMoved) { this.csm.lightDirection.copy(this.wantDir); this.lastDir.copy(this.wantDir); }
    cam.updateMatrixWorld();
    this.setupScene();
    this.csm.update();
    if (!this.enabled || this.intensity <= 0) { if (dirMoved) this.markDirty(); return 0; }   // nothing to draw now, but the maps will be stale

    const periodic = nowMs - this.lastRefreshMs > refreshEveryMs;
    const dir = this.csm.lightDirection;
    let n = 0;
    for (let i = 0; i < this.lights.length; i++) {
      const l = this.lights[i];
      // Where the light's axis crosses the plane through the origin: it changes only when the window slides sideways, and not when
      // it slides along the light's direction (which changes nothing in the map).
      this._q.copy(l.position).addScaledVector(dir, -l.position.dot(dir));
      const moved = !(this._q.distanceTo(this.lastPos[i]) <= 0.5 * this.texelM[i]);      // NaN-safe: the first time counts as moved
      const casters = this.pending[i] && nowMs - this.lastDrawMs[i] >= (this.dirtyMinMs[i] ?? 0);
      if (casters || dirMoved || moved || (periodic && i === this.lights.length - 1)) {
        l.shadow.needsUpdate = true; this.redraws[i]++; n++;
        this.lastPos[i].copy(this._q); this.pending[i] = false; this.lastDrawMs[i] = nowMs;
      }
    }
    if (periodic) this.lastRefreshMs = nowMs;
    return n;
  }

  /** Per-cascade depth range and biases, from each cascade's extent. */
  private fitCascades() {
    this.lights.forEach((l, i) => {
      const cam = l.shadow.camera as THREE.OrthographicCamera;
      const width = cam.right - cam.left;
      const texel = width / this.mapSize;
      cam.near = 1; cam.far = this.margin + width + 10;               // from the light's standoff down through the slice
      cam.updateProjectionMatrix();
      const range = cam.far - cam.near;
      l.shadow.bias = -Math.max(this.biasM, 0.1 * texel) / range;
      l.shadow.normalBias = this.nbTexels * texel;
      this.texelM[i] = texel;
    });
  }

  /** Make every lit material in the scene use the cascades. Cheap when nothing is new. */
  setupScene() {
    this.scene.traverse((o: any) => {
      if (!o.isMesh && !o.isPoints && !o.isLine) return;
      const ms: THREE.Material[] = Array.isArray(o.material) ? o.material : [o.material];
      for (const m of ms) this.setupMaterial(m);
    });
  }

  private setupMaterial(m: THREE.Material) {
    if (!m || this.done.has(m)) return;
    this.done.add(m);
    const lit = LIT_FLAGS.some(f => (m as any)[f]) || (m as any).lights === true;
    if (!lit) return;
    const prev = m.onBeforeCompile;
    const prevKey = m.customProgramCacheKey();
    this.csm.setupMaterial(m);                  // overwrites onBeforeCompile
    const csmHook = m.onBeforeCompile;
    const cloud = this.cloudOk ? this.cloudUniforms : null;
    m.onBeforeCompile = function (this: THREE.Material, sh: any, r: any) {
      prev.call(this, sh, r); csmHook.call(this, sh, r);
      // the cloud shadow's lookups (cloudShadow.ts) go into this material's program if it has a texture unit to spare for the map (the units a program can read are 16 on many GPUs, and the water has nearly all of them)
      if (cloud) {
        const used = samplerUnitsUsed(sh), cap: number = r?.capabilities?.maxTextures ?? 16;
        if (used + 1 <= cap) { sh.defines = sh.defines || {}; sh.defines.USE_CLOUD_SHADOW = 1; Object.assign(sh.uniforms, cloud); }
        else console.warn(`[cloud shadow] not on "${(this as any).name || this.type}": it reads ${used} of ${cap} texture units already`);
      }
    };
    m.customProgramCacheKey = () => prevKey + '|csm' + (cloud ? '|cs' : '');
    m.needsUpdate = true;
  }

  dispose() {
    this.csm.remove();                          // not csm.dispose(): that would strip the materials' own onBeforeCompile too
  }
}
